import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Shield, Star, CheckCircle, MapPin, Building2, Lock, Users, DollarSign, ArrowRight } from 'lucide-react';
import LocationAutofill from '../components/LocationAutofill';
import { useListingStore } from '../stores/listingStore';

const Home = () => {
  const navigate = useNavigate();
  const [searchLocation, setSearchLocation] = useState('');
  const { listings } = useListingStore();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/listings?location=${encodeURIComponent(searchLocation)}`);
  };

  // Calculate active listing counts by area
  const listingCounts = useMemo(() => {
    const activeListings = listings.filter(listing => 
      listing.status === 'active' && !listing.isLeased
    );

    const counts = {
      "Manhattan, NY": activeListings.filter(l => 
        l.location.toLowerCase().includes('manhattan') ||
        l.displayLocation.toLowerCase().includes('manhattan')
      ).length,
      "Brooklyn, NY": activeListings.filter(l => 
        l.location.toLowerCase().includes('brooklyn') ||
        l.displayLocation.toLowerCase().includes('brooklyn')
      ).length,
      "Miami Beach, FL": activeListings.filter(l => 
        l.location.toLowerCase().includes('miami beach') ||
        l.displayLocation.toLowerCase().includes('miami beach')
      ).length,
      "Downtown Miami, FL": activeListings.filter(l => 
        (l.location.toLowerCase().includes('miami') ||
        l.displayLocation.toLowerCase().includes('miami')) &&
        !l.location.toLowerCase().includes('miami beach') &&
        !l.displayLocation.toLowerCase().includes('miami beach')
      ).length
    };

    return counts;
  }, [listings]);

  const popularCities = [
    {
      name: "Manhattan, NY",
      count: listingCounts["Manhattan, NY"]
    },
    {
      name: "Brooklyn, NY",
      count: listingCounts["Brooklyn, NY"]
    },
    {
      name: "Miami Beach, FL",
      count: listingCounts["Miami Beach, FL"]
    },
    {
      name: "Downtown Miami, FL",
      count: listingCounts["Downtown Miami, FL"]
    }
  ];

  const features = [
    {
      icon: <Lock className="h-12 w-12 text-indigo-600" />,
      title: "Privacy Protected",
      description: "Smart privacy features protect your identity. Your listing stays discreet and off your landlord's radar."
    },
    {
      icon: <Users className="h-12 w-12 text-indigo-600" />,
      title: "Verified Subletters",
      description: "All potential subletters go through our verification process for your peace of mind."
    },
    {
      icon: <DollarSign className="h-12 w-12 text-indigo-600" />,
      title: "Free to List",
      description: "No upfront costs. List your place for free and only pay when you find a match."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <div 
        className="relative bg-cover bg-center min-h-[85vh] flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url("https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80")',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Find Your Perfect <span className="text-indigo-400">Sublease</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Connect with verified subletters and find your next home with confidence.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="bg-white p-2 rounded-lg shadow-lg flex flex-col md:flex-row gap-2 mb-8">
              <div className="flex-1">
                <LocationAutofill
                  value={searchLocation}
                  onChange={(value) => setSearchLocation(value)}
                  onSelect={(location) => {
                    setSearchLocation(location.name);
                    navigate(`/listings?location=${encodeURIComponent(location.name)}`);
                  }}
                  placeholder="Enter city or neighborhood"
                  className="w-full pl-10 pr-4 py-3 rounded-md border-0 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-8 py-3 rounded-md hover:bg-indigo-700 transition-colors flex items-center justify-center whitespace-nowrap"
              >
                <Search className="h-5 w-5 mr-2" />
                Find Listings
              </button>
            </form>

            <div className="flex flex-wrap gap-4">
              {popularCities.map((city) => (
                <button
                  key={city.name}
                  onClick={() => {
                    setSearchLocation(city.name);
                    navigate(`/listings?location=${encodeURIComponent(city.name)}`);
                  }}
                  className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-white/20 transition-colors"
                >
                  {city.name} ({city.count})
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* List Property CTA */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-white">
                <h2 className="text-2xl font-bold mb-2">Need to Get Out of Your Lease Early?</h2>
                <p className="text-gray-300">Traveling or moving? Sublease your place discreetly and find reliable subletters.</p>
              </div>
              <Link
                to="/list-property"
                className="flex items-center bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors group"
              >
                List Your Place
                <ArrowRight className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose SubleaseHub?
            </h2>
            <p className="text-xl text-gray-600">
              The most trusted platform for discreet and secure subleasing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Your Privacy is Our Priority
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We understand the need for discretion when subletting. Our platform includes advanced privacy features to protect your identity and keep your listing private.
              </p>
              <ul className="space-y-4">
                {[
                  "Exact address hidden until matched",
                  "Photos automatically scrubbed of identifying details",
                  "Private messaging system",
                  "Control over listing visibility",
                  "Verified users only"
                ].map((item, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1554995207-c18c203602cb?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                alt="Secure apartment" 
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-indigo-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-xl text-indigo-100">
                Join thousands who trust SubleaseHub
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/list-property"
                className="inline-block bg-white text-indigo-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-indigo-50 transition-colors"
              >
                List Your Place
              </Link>
              <Link
                to="/listings"
                className="inline-block bg-indigo-500 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-indigo-400 transition-colors"
              >
                Browse Listings
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;